/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';

export interface FormPdfDialogData {
  pdf: Blob;
  fileName: string;
}

/**
 * A printed form shown in the browser's own PDF viewer. Shown in place rather
 * than in a new tab: a tab opened after waiting for the PDF is what pop-up
 * blockers stop.
 */
@Component({
  selector: 'app-form-pdf-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-frmpdf">
      <div class="aca-frmpdf__bar">
        <h2 mat-dialog-title class="aca-frmpdf__title">{{ 'DMS.FORMS.PDF_PREVIEW' | translate }}</h2>
        <a class="aca-frmpdf__btn" [href]="url" [attr.download]="data.fileName">
          <mat-icon>download</mat-icon>{{ 'DMS.FORMS.DOWNLOAD' | translate }}
        </a>
        <button type="button" class="aca-frmpdf__close" mat-dialog-close [attr.aria-label]="'DMS.FORMS.CLOSE' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <iframe class="aca-frmpdf__frame" [src]="safeUrl" [title]="data.fileName"></iframe>
    </div>
  `
})
export class FormPdfDialogComponent implements OnDestroy {
  readonly data = inject<FormPdfDialogData>(MAT_DIALOG_DATA);
  /** Always a PDF, whatever type the response said — a page of HTML must never render in this frame. */
  readonly url = URL.createObjectURL(new Blob([this.data.pdf], { type: 'application/pdf' }));
  /** A blob: URL this page just made from the PDF, nothing a person typed. */
  readonly safeUrl: SafeResourceUrl = inject(DomSanitizer).bypassSecurityTrustResourceUrl(this.url);

  ngOnDestroy(): void {
    URL.revokeObjectURL(this.url);
  }
}
